import React, { JSX, useEffect } from 'react';

import { PortaledLayer } from '../../shared/PortaledLayer';
import { BTN_ICON } from '../utils';
import { markRaceStackedOverlayJustClosed } from '../utils/raceDetailPortalHelpers';
import DevicePackageAssignmentDialog from './DevicePackageAssignmentDialog';

type Props = {
  open: boolean;
  eventId: string;
  raceId: string;
  raceName?: string;
  assignmentsLocked?: boolean;
  onClose: () => void;
  onSaved?: (eventId: string) => void;
};

export default function DevicePackageAssignmentPortal(props: Props): JSX.Element | null {
  const {
    open,
    eventId,
    raceId,
    raceName,
    assignmentsLocked = false,
    onClose,
    onSaved,
  } = props;

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      const stacked = document.querySelectorAll('[data-race-stacked-overlay]');
      if (stacked.length > 1) return;
      markRaceStackedOverlayJustClosed();
      onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <PortaledLayer>
      <div
        data-race-stacked-overlay=""
        className="fixed inset-0 z-[5100] flex items-center justify-center bg-black/40 p-3 dark:bg-black/70 sm:p-6"
        role="presentation"
        onClick={(e) => {
          if (e.target !== e.currentTarget) return;
          markRaceStackedOverlayJustClosed();
          onClose();
        }}
      >
        <div
          className="flex max-h-[min(92vh,900px)] w-full max-w-[min(1100px,100vw-1.5rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-gray-800 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          role="dialog"
          aria-modal="true"
          aria-labelledby="device-package-assign-portal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
            <div className="min-w-0">
              <h2
                id="device-package-assign-portal-title"
                className="text-sm font-semibold text-gray-900 dark:text-slate-100"
              >
                Scan &amp; assign devices
              </h2>
              {raceName ? (
                <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-slate-400">{raceName}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className={BTN_ICON}
              aria-label="Close"
              title="Close"
            >
              ✕
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <DevicePackageAssignmentDialog
              eventId={eventId}
              raceId={raceId}
              raceName={raceName}
              assignmentsLocked={assignmentsLocked}
              onSaved={onSaved}
              onClose={onClose}
            />
          </div>
        </div>
      </div>
    </PortaledLayer>
  );
}
